import React, { useState, useMemo } from 'react';
import { useAppData } from '@/context/AppContext';
import Card from '@/components/Card';
import Modal from '@/components/Modal';
import { Plus, Trash2, Check, Moon, Zap, Dumbbell, MessageSquare, FileText, X, Pencil, ChevronLeft, ChevronRight } from 'lucide-react';
import styles from './ExercisePage.module.css';

const DAY_LABELS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

const DAY_MAP: Record<string, number> = {
  monday: 0, mon: 0,
  tuesday: 1, tue: 1, tues: 1,
  wednesday: 2, wed: 2,
  thursday: 3, thu: 3, thur: 3, thurs: 3,
  friday: 4, fri: 4,
  saturday: 5, sat: 5,
  sunday: 6, sun: 6,
};

const CARDIO_KEYWORDS = [
  'walk', 'swim', 'cycl', 'bike', 'jog', 'run', 'pool', 'aqua',
  'row', 'elliptical', 'treadmill', 'stair', 'aerobic', 'cardio', 'water',
  'cross train',
];

function isCardioEx(name: string): boolean {
  const lower = name.toLowerCase();
  return CARDIO_KEYWORDS.some(k => lower.includes(k));
}

function extractDays(text: string): number[] {
  const lower = text.toLowerCase();
  const days = new Set<number>();
  Object.entries(DAY_MAP).forEach(([dayName, idx]) => {
    const re = new RegExp(`\\b${dayName}\\b`, 'i');
    if (re.test(lower)) days.add(idx);
  });
  if (days.size > 0) return Array.from(days).sort((a, b) => a - b);
  if (/\b(daily|every\s*day|each\s*day|7\s*days?\s*(a|per)\s*week)\b/i.test(lower)) return [0, 1, 2, 3, 4, 5, 6];
  if (/\btwice\s*(?:a|per)\s*week\b/i.test(lower)) return [0, 3];
  if (/\bevery\s*other\s*day\b/i.test(lower)) return [0, 2, 4, 6];
  const freqMatch = lower.match(/(\d+)\s*(?:x|times?|days?)\s*(?:a|per)\s*week/i);
  if (freqMatch) {
    const n = Math.min(7, parseInt(freqMatch[1], 10));
    const spread: number[] = [];
    for (let i = 0; i < n; i++) spread.push(Math.round((i * 7) / n) % 7);
    return spread.sort((a, b) => a - b);
  }
  return [];
}

function extractSetsReps(text: string): { sets: number; reps: number; duration: number } {
  const lower = text.toLowerCase();
  let sets = 3, reps = 10, duration = 0;
  const durMinMatch = lower.match(/(\d+)\s*(?:minutes?|mins?)/i);
  const durSecMatch = lower.match(/(\d+)\s*(?:seconds?|secs?)/i);
  if (durMinMatch) duration = parseInt(durMinMatch[1], 10);
  else if (durSecMatch) duration = Math.max(1, Math.round(parseInt(durSecMatch[1], 10) / 60));
  const setsMatch = lower.match(/(\d+)\s*sets?/i);
  if (setsMatch) sets = parseInt(setsMatch[1], 10);
  const repsMatch = lower.match(/(\d+)\s*(?:reps?|repetitions?)/i);
  if (repsMatch) reps = parseInt(repsMatch[1], 10);
  const xMatch = lower.match(/(\d+)\s*[x×]\s*(\d+)/i);
  if (xMatch && !setsMatch) { sets = parseInt(xMatch[1], 10); reps = parseInt(xMatch[2], 10); }
  return { sets, reps, duration };
}

interface ParsedExercise {
  name: string;
  exerciseType: 'cardio' | 'strength';
  sets: number;
  reps: number;
  duration: number;
  scheduleDays: number[];
  notes: string;
}

function parsePhysioText(text: string): ParsedExercise[] {
  const lines = text.split('\n').map(l => l.trim()).filter(l => l.length > 0);
  const exercises: ParsedExercise[] = [];
  const namePattern = /^(?:\d+[\.\)]\s*)?(?:[-•]\s*)?(.+)/;
  for (const line of lines) {
    const nameMatch = line.match(namePattern);
    if (!nameMatch) continue;
    const rawName = nameMatch[1].replace(/[:;,].*$/, '').trim();
    if (rawName.length < 3) continue;
    if (/^\d+$/.test(rawName)) continue;
    const exerciseType = isCardioEx(rawName) ? 'cardio' : 'strength';
    const { sets, reps, duration } = extractSetsReps(line);
    const scheduleDays = extractDays(line);
    exercises.push({ name: rawName, exerciseType, sets, reps, duration, scheduleDays, notes: '' });
  }
  return exercises;
}

function getTodayIndex(): number {
  const jsDay = new Date().getDay();
  return jsDay === 0 ? 6 : jsDay - 1;
}

export default function ExercisePage() {
  const {
    prescribedExercises,
    exerciseCompletions,
    addExercise,
    updateExercise,
    deleteExercise,
    toggleExerciseCompletion,
    updateExerciseCompletionNotes,
  } = useAppData();

  const [selectedDate, setSelectedDate] = useState<Date>(() => new Date());
  const [modalOpen, setModalOpen] = useState(false);
  const [editingExercise, setEditingExercise] = useState<any | null>(null);
  const [exerciseType, setExerciseType] = useState<'cardio' | 'strength'>('strength');
  const [formName, setFormName] = useState('');
  const [formSets, setFormSets] = useState('3');
  const [formReps, setFormReps] = useState('10');
  const [formWeight, setFormWeight] = useState('0');
  const [formDuration, setFormDuration] = useState('30');
  const [formNotes, setFormNotes] = useState('');
  const [formDays, setFormDays] = useState<number[]>([getTodayIndex()]);
  const [submitting, setSubmitting] = useState(false);
  const [completionNoteId, setCompletionNoteId] = useState<string | null>(null);
  const [completionNoteText, setCompletionNoteText] = useState('');

  const [importOpen, setImportOpen] = useState(false);
  const [importText, setImportText] = useState('');
  const [parsedExercises, setParsedExercises] = useState<ParsedExercise[]>([]);
  const [importStep, setImportStep] = useState<'paste' | 'preview'>('paste');
  const [importing, setImporting] = useState(false);

  const dayOfWeekIndex = useMemo(() => (selectedDate.getDay() + 6) % 7, [selectedDate]);

  const isToday = useMemo(() => {
    const t = new Date();
    return selectedDate.toDateString() === t.toDateString();
  }, [selectedDate]);

  const dateString = useMemo(() => selectedDate.toISOString().split('T')[0], [selectedDate]);

  const exercisesForDay = useMemo(() => {
    return prescribedExercises.filter((e: any) =>
      e.scheduleDays && e.scheduleDays.includes(dayOfWeekIndex)
    );
  }, [prescribedExercises, dayOfWeekIndex]);

  const getCompletion = (exerciseId: string) => {
    return exerciseCompletions.find(
      (c: any) => c.exerciseId === exerciseId && c.date === dateString
    );
  };

  const isCompleted = (exerciseId: string) => !!getCompletion(exerciseId);

  const handleToggle = async (exerciseId: string) => {
    await toggleExerciseCompletion(exerciseId, dateString);
  };

  const handleDelete = async (id: string) => {
    await deleteExercise(id);
  };

  const resetForm = () => {
    setFormName('');
    setExerciseType('strength');
    setFormSets('3');
    setFormReps('10');
    setFormWeight('0');
    setFormDuration('30');
    setFormNotes('');
    setFormDays([dayOfWeekIndex]);
    setEditingExercise(null);
  };

  const openModal = () => {
    resetForm();
    setModalOpen(true);
  };

  const openEditModal = (exercise: any) => {
    setEditingExercise(exercise);
    setFormName(exercise.name);
    setExerciseType(exercise.exerciseType || 'strength');
    setFormSets(String(exercise.sets || 3));
    setFormReps(String(exercise.reps || 10));
    setFormWeight(String(exercise.weight || 0));
    setFormDuration(String(exercise.duration || 30));
    setFormNotes(exercise.notes || '');
    setFormDays(exercise.scheduleDays || []);
    setModalOpen(true);
  };

  const handleSubmit = async () => {
    if (!formName.trim() || formDays.length === 0) return;
    setSubmitting(true);
    try {
      if (editingExercise) {
        await updateExercise(
          editingExercise.id,
          formName.trim(),
          exerciseType === 'strength' ? (parseInt(formSets) || 0) : 0,
          exerciseType === 'strength' ? (parseInt(formReps) || 0) : 0,
          exerciseType === 'cardio' ? (parseInt(formDuration) || 0) : 0,
          formDays,
          exerciseType,
          formNotes.trim(),
          exerciseType === 'strength' ? (parseInt(formWeight) || 0) : 0
        );
      } else {
        await addExercise(
          formName.trim(),
          exerciseType === 'strength' ? (parseInt(formSets) || 0) : 0,
          exerciseType === 'strength' ? (parseInt(formReps) || 0) : 0,
          exerciseType === 'cardio' ? (parseInt(formDuration) || 0) : 0,
          formDays,
          exerciseType,
          formNotes.trim(),
          exerciseType === 'strength' ? (parseInt(formWeight) || 0) : 0
        );
      }
      setModalOpen(false);
      resetForm();
    } finally {
      setSubmitting(false);
    }
  };

  const toggleFormDay = (day: number) => {
    setFormDays((prev) =>
      prev.includes(day) ? prev.filter((d) => d !== day) : [...prev, day]
    );
  };

  const handleCompletionNoteSave = async (exerciseId: string) => {
    await updateExerciseCompletionNotes(exerciseId, dateString, completionNoteText);
    setCompletionNoteId(null);
    setCompletionNoteText('');
  };

  const openCompletionNote = (exerciseId: string) => {
    const completion = getCompletion(exerciseId);
    setCompletionNoteId(exerciseId);
    setCompletionNoteText(completion?.notes || '');
  };

  const openImport = () => {
    setImportText('');
    setParsedExercises([]);
    setImportStep('paste');
    setImportOpen(true);
  };

  const handleParse = () => {
    const results = parsePhysioText(importText);
    setParsedExercises(results);
    setImportStep('preview');
  };

  const updateParsedDays = (i: number, day: number) => {
    setParsedExercises(prev => prev.map((ex, idx) => {
      if (idx !== i) return ex;
      const days = ex.scheduleDays.includes(day)
        ? ex.scheduleDays.filter(d => d !== day)
        : [...ex.scheduleDays, day].sort((a, b) => a - b);
      return { ...ex, scheduleDays: days };
    }));
  };

  const handleImportAll = async () => {
    const valid = parsedExercises.filter(e => e.name.trim().length > 0);
    if (valid.length === 0) return;
    setImporting(true);
    try {
      for (const ex of valid) {
        await addExercise(
          ex.name,
          ex.exerciseType === 'strength' ? ex.sets : 0,
          ex.exerciseType === 'strength' ? ex.reps : 0,
          ex.exerciseType === 'cardio' ? ex.duration : 0,
          ex.scheduleDays.length > 0 ? ex.scheduleDays : [0, 1, 2, 3, 4],
          ex.exerciseType,
          ex.notes,
          0
        );
      }
      setImportOpen(false);
    } finally {
      setImporting(false);
    }
  };

  const removeParsed = (i: number) => {
    setParsedExercises(prev => prev.filter((_, idx) => idx !== i));
  };

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <h1 className={styles.title}>Exercises</h1>
        <div style={{ display: 'flex', gap: 8 }}>
          <button className={styles.addButton} style={{ background: 'var(--bg-secondary)', color: 'var(--text)', border: '1px solid var(--border)' }} onClick={openImport}>
            <FileText size={16} />
            Import
          </button>
          <button className={styles.addButton} onClick={openModal}>
            <Plus size={18} />
            Add Exercise
          </button>
        </div>
      </div>

      <div className={styles.dateNav}>
        <button className={styles.dateNavBtn} onClick={() => { const d = new Date(selectedDate); d.setDate(d.getDate() - 1); setSelectedDate(d); }}>
          <ChevronLeft size={20} />
        </button>
        <div className={styles.dateNavCenter}>
          <div className={styles.dateNavLabel}>
            {DAY_LABELS[dayOfWeekIndex]}, {selectedDate.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })}
          </div>
          {!isToday && (
            <button className={styles.dateNavToday} onClick={() => setSelectedDate(new Date())}>Back to Today</button>
          )}
        </div>
        <button className={styles.dateNavBtn} onClick={() => { const d = new Date(selectedDate); d.setDate(d.getDate() + 1); setSelectedDate(d); }}>
          <ChevronRight size={20} />
        </button>
      </div>

      {exercisesForDay.length === 0 ? (
        <Card>
          <div className={styles.restDay}>
            <Moon size={48} className={styles.restDayIcon} />
            <div className={styles.restDayTitle}>Rest Day</div>
            <div className={styles.restDayText}>
              No exercises scheduled for {DAY_LABELS[dayOfWeekIndex]}. Take it easy and recover!
            </div>
          </div>
        </Card>
      ) : (
        <div className={styles.exerciseList}>
          {exercisesForDay.map((exercise: any) => {
            const done = isCompleted(exercise.id);
            const completion = getCompletion(exercise.id);
            const isCardio = exercise.exerciseType === 'cardio';
            return (
              <Card key={exercise.id}>
                <div className={styles.exerciseCard}>
                  <button
                    className={`${styles.checkbox} ${done ? styles.checkboxDone : ''}`}
                    onClick={() => handleToggle(exercise.id)}
                  >
                    {done ? <Check size={14} /> : null}
                  </button>
                  <div className={styles.exerciseInfo}>
                    <div className={styles.exerciseNameRow}>
                      <span className={`${styles.typeIndicator} ${isCardio ? styles.typeCardio : styles.typeStrength}`}>
                        {isCardio ? <Zap size={12} /> : <Dumbbell size={12} />}
                        {isCardio ? 'Cardio' : 'Strength'}
                      </span>
                      <span className={`${styles.exerciseName} ${done ? styles.exerciseNameDone : ''}`}>
                        {exercise.name}
                      </span>
                    </div>
                    <div className={styles.exerciseDetails}>
                      {isCardio ? (
                        exercise.duration > 0 ? `${exercise.duration} min` : 'No duration set'
                      ) : (
                        <>
                          {exercise.sets > 0 && exercise.reps > 0
                            ? `${exercise.sets} sets x ${exercise.reps} reps`
                            : null}
                          {exercise.weight > 0 ? ` @ ${exercise.weight}kg` : null}
                          {exercise.sets === 0 && exercise.reps === 0 ? 'No details' : null}
                        </>
                      )}
                    </div>
                    {exercise.notes ? (
                      <div className={styles.exerciseNotes}>{exercise.notes}</div>
                    ) : null}
                    {done && completionNoteId === exercise.id ? (
                      <div className={styles.completionNoteForm}>
                        <input
                          className={styles.completionNoteInput}
                          type="text"
                          value={completionNoteText}
                          onChange={(e) => setCompletionNoteText(e.target.value)}
                          placeholder="How did it go? Any pain or notes..."
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') handleCompletionNoteSave(exercise.id);
                          }}
                        />
                        <button
                          className={styles.completionNoteSave}
                          onClick={() => handleCompletionNoteSave(exercise.id)}
                        >
                          Save
                        </button>
                      </div>
                    ) : done ? (
                      <button
                        className={styles.completionNoteToggle}
                        onClick={() => openCompletionNote(exercise.id)}
                      >
                        <MessageSquare size={12} />
                        {completion?.notes ? completion.notes : 'Add note'}
                      </button>
                    ) : null}
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                    <button className={styles.deleteButton} style={{ color: 'var(--text-secondary)' }} onClick={() => openEditModal(exercise)}>
                      <Pencil size={15} />
                    </button>
                    <button className={styles.deleteButton} onClick={() => handleDelete(exercise.id)}>
                      <Trash2 size={16} />
                    </button>
                  </div>
                </div>
              </Card>
            );
          })}
        </div>
      )}

      {/* Add / Edit Exercise Modal */}
      <Modal isOpen={modalOpen} onClose={() => { setModalOpen(false); resetForm(); }} title={editingExercise ? 'Edit Exercise' : 'Add Exercise'}>
        <div className={styles.typeToggle}>
          <button
            className={`${styles.typeToggleButton} ${exerciseType === 'strength' ? styles.typeToggleActive : ''}`}
            onClick={() => setExerciseType('strength')}
          >
            <Dumbbell size={16} />
            Strength
          </button>
          <button
            className={`${styles.typeToggleButton} ${exerciseType === 'cardio' ? styles.typeToggleActive : ''}`}
            onClick={() => setExerciseType('cardio')}
          >
            <Zap size={16} />
            Cardio
          </button>
        </div>

        <div className={styles.formGroup}>
          <label className={styles.label}>Exercise Name</label>
          <input
            className={styles.input}
            type="text"
            value={formName}
            onChange={(e) => setFormName(e.target.value)}
            placeholder={exerciseType === 'cardio' ? 'e.g., Swimming' : 'e.g., Leg Press'}
          />
        </div>

        {exerciseType === 'strength' ? (
          <div className={styles.formRow}>
            <div className={styles.formGroup}>
              <label className={styles.label}>Sets</label>
              <input className={styles.input} type="number" value={formSets} onChange={(e) => setFormSets(e.target.value)} min="0" />
            </div>
            <div className={styles.formGroup}>
              <label className={styles.label}>Reps</label>
              <input className={styles.input} type="number" value={formReps} onChange={(e) => setFormReps(e.target.value)} min="0" />
            </div>
            <div className={styles.formGroup}>
              <label className={styles.label}>Weight (kg)</label>
              <input className={styles.input} type="number" value={formWeight} onChange={(e) => setFormWeight(e.target.value)} min="0" />
            </div>
          </div>
        ) : (
          <div className={styles.formGroup}>
            <label className={styles.label}>Duration (minutes)</label>
            <input className={styles.input} type="number" value={formDuration} onChange={(e) => setFormDuration(e.target.value)} min="0" />
          </div>
        )}

        <div className={styles.formGroup}>
          <label className={styles.label}>Notes (optional)</label>
          <textarea
            className={`${styles.input} ${styles.textarea}`}
            value={formNotes}
            onChange={(e) => setFormNotes(e.target.value)}
            placeholder="Any specific instructions from your physio..."
            rows={3}
          />
        </div>

        <div className={styles.formGroup}>
          <label className={styles.label}>How many days a week?</label>
          <div className={styles.daysCount}>{formDays.length} day{formDays.length !== 1 ? 's' : ''} selected</div>
        </div>

        <div className={styles.formGroup}>
          <label className={styles.label}>Select Days</label>
          <div className={styles.modalDaySelector}>
            {DAY_LABELS.map((label, i) => (
              <button
                key={i}
                type="button"
                className={`${styles.modalDayButton} ${formDays.includes(i) ? styles.modalDayButtonActive : ''}`}
                onClick={() => toggleFormDay(i)}
              >
                {label}
              </button>
            ))}
          </div>
        </div>
        <button
          className={styles.submitButton}
          onClick={handleSubmit}
          disabled={submitting || !formName.trim() || formDays.length === 0}
        >
          {submitting ? 'Saving...' : editingExercise ? 'Save Changes' : 'Add Exercise'}
        </button>
      </Modal>

      {/* Physio Import Modal */}
      <Modal isOpen={importOpen} onClose={() => setImportOpen(false)} title={importStep === 'paste' ? 'Import from Physio' : `${parsedExercises.length} Exercise${parsedExercises.length !== 1 ? 's' : ''} Found`}>
        {importStep === 'paste' ? (
          <>
            <p style={{ color: 'var(--text-secondary)', fontSize: 14, marginBottom: 16, lineHeight: 1.5 }}>
              Copy your exercise instructions from a physio email, letter, or document and paste them below. beryl will extract your exercises automatically.
            </p>
            <textarea
              style={{
                width: '100%',
                minHeight: 180,
                background: 'var(--bg-secondary)',
                border: '1px solid var(--border)',
                borderRadius: 8,
                padding: '12px',
                color: 'var(--text)',
                fontSize: 14,
                lineHeight: 1.6,
                resize: 'vertical',
                boxSizing: 'border-box',
                outline: 'none',
                fontFamily: 'inherit',
              }}
              value={importText}
              onChange={e => setImportText(e.target.value)}
              placeholder={'e.g.\n1. Calf raises: 3 sets x 15 reps, Mon/Wed/Fri\n2. Walking: 30 minutes daily\n3. Single leg balance: 3 x 30 sec, 3 times a week'}
            />
            <button
              className={styles.submitButton}
              onClick={handleParse}
              disabled={!importText.trim()}
              style={{ marginTop: 16 }}
            >
              Extract Exercises
            </button>
          </>
        ) : (
          <>
            {parsedExercises.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '32px 0', color: 'var(--text-secondary)' }}>
                <p style={{ marginBottom: 16 }}>No exercises could be extracted. Try adding more detail to your text.</p>
                <button className={styles.submitButton} style={{ background: 'var(--bg-secondary)', color: 'var(--text)', border: '1px solid var(--border)' }} onClick={() => setImportStep('paste')}>
                  Go Back
                </button>
              </div>
            ) : (
              <>
                <p style={{ color: 'var(--text-secondary)', fontSize: 13, marginBottom: 12 }}>
                  Review and adjust the days for each exercise before importing.
                </p>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 12, marginBottom: 16, maxHeight: 380, overflowY: 'auto' }}>
                  {parsedExercises.map((ex, i) => (
                    <div key={i} style={{
                      background: 'var(--bg-secondary)',
                      borderRadius: 10,
                      padding: '12px 14px',
                    }}>
                      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10, marginBottom: 8 }}>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ fontWeight: 600, fontSize: 14, marginBottom: 2 }}>{ex.name}</div>
                          <div style={{ fontSize: 12, color: 'var(--text-secondary)' }}>
                            <span style={{
                              display: 'inline-flex', alignItems: 'center', gap: 3,
                              background: ex.exerciseType === 'cardio' ? 'var(--tertiary)22' : 'var(--primary)22',
                              color: ex.exerciseType === 'cardio' ? 'var(--tertiary)' : 'var(--primary)',
                              borderRadius: 4, padding: '1px 6px', fontSize: 11, fontWeight: 600, marginRight: 6
                            }}>
                              {ex.exerciseType === 'cardio' ? 'Cardio' : 'Strength'}
                            </span>
                            {ex.exerciseType === 'cardio'
                              ? (ex.duration > 0 ? `${ex.duration} min` : '')
                              : `${ex.sets} sets × ${ex.reps} reps`}
                          </div>
                        </div>
                        <button onClick={() => removeParsed(i)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-secondary)', padding: 2, flexShrink: 0 }}>
                          <X size={14} />
                        </button>
                      </div>
                      {/* Day picker */}
                      <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                        {DAY_LABELS.map((label, d) => (
                          <button
                            key={d}
                            onClick={() => updateParsedDays(i, d)}
                            style={{
                              padding: '3px 8px',
                              borderRadius: 6,
                              border: `1px solid ${ex.scheduleDays.includes(d) ? 'var(--primary)' : 'var(--border)'}`,
                              background: ex.scheduleDays.includes(d) ? 'var(--primary)' : 'transparent',
                              color: ex.scheduleDays.includes(d) ? '#fff' : 'var(--text-secondary)',
                              fontSize: 11,
                              fontWeight: 600,
                              cursor: 'pointer',
                            }}
                          >
                            {label}
                          </button>
                        ))}
                      </div>
                      {ex.scheduleDays.length === 0 && (
                        <div style={{ fontSize: 11, color: 'var(--error)', marginTop: 4 }}>
                          Select at least one day — will default to Mon–Fri
                        </div>
                      )}
                    </div>
                  ))}
                </div>
                <div style={{ display: 'flex', gap: 8 }}>
                  <button
                    style={{ flex: 1, background: 'var(--bg-secondary)', color: 'var(--text)', border: '1px solid var(--border)', borderRadius: 8, padding: '10px', fontWeight: 600, fontSize: 14, cursor: 'pointer' }}
                    onClick={() => setImportStep('paste')}
                  >
                    Back
                  </button>
                  <button
                    className={styles.submitButton}
                    style={{ flex: 2, margin: 0 }}
                    onClick={handleImportAll}
                    disabled={importing || parsedExercises.length === 0}
                  >
                    {importing ? 'Importing...' : `Add ${parsedExercises.length} Exercise${parsedExercises.length !== 1 ? 's' : ''}`}
                  </button>
                </div>
              </>
            )}
          </>
        )}
      </Modal>
    </div>
  );
}
